import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import type { TrackingCore } from "./core";

/**
 * Onboarding & account-lifecycle events: the quiz, the paginated quiz stepper
 * funnel, the first-dinner welcome card, and the Clerk→Firebase/Apple account
 * migration flow. Merged onto the tracking singleton in index.ts.
 */
export const onboardingEvents = {
  quizStarted(this: TrackingCore) {
    this.track("quiz_started");
  },

  quizQuestionAnswered(
    this: TrackingCore,
    questionId: string,
    questionIndex: number,
    category: string,
    totalQuestions: number,
  ) {
    this.track("quiz_question_answered", {
      question_id: questionId,
      question_index: questionIndex,
      question_category: category,
      total_questions: totalQuestions,
      progress_pct: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    });
  },

  quizCompleted(this: TrackingCore) {
    this.track("quiz_completed");

    // Fire native Facebook CompleteRegistration (quiz = onboarding completion)
    if (bridge.isNative) {
      bridge.emit(
        BridgeChannels.TRACKING,
        BridgeActions.TRACK_COMPLETED_REGISTRATION,
        { registrationMethod: "quiz" }
      );
    }
  },

  // ─── Paginated quiz stepper funnel ────────────────────────────────────────
  // Funnel events for the OnboardingQuiz (?stepper=1) experience. Every event
  // includes `audience` (men / sapphic / general) so we can read
  // conversion per ad-targeting variant.

  quizStepperExposed(this: TrackingCore, audience: string) {
    this.track("quiz_stepper_exposed", { audience });
  },

  quizStepperStepViewed(this: TrackingCore, args: {
    stepIndex: number;
    totalSteps: number;
    stepType: "question" | "interstitial" | "age-intro" | "community-intro";
    stepKey: string;
    section: string;
    audience: string;
  }) {
    this.track("quiz_stepper_step_viewed", {
      step_index: args.stepIndex,
      total_steps: args.totalSteps,
      step_type: args.stepType,
      step_key: args.stepKey,
      section: args.section,
      progress_pct: Math.round(((args.stepIndex + 1) / args.totalSteps) * 100),
      audience: args.audience,
    });
  },

  quizStepperAgePicked(this: TrackingCore, bucket: string, birthdayIso: string, audience: string) {
    this.track("quiz_stepper_age_picked", {
      age_bucket: bucket,
      birthday_iso: birthdayIso,
      audience,
    });
  },

  quizStepperReachedAuthGate(this: TrackingCore, audience: string) {
    this.track("quiz_stepper_reached_auth_gate", { audience });
  },

  quizStepperAuthClicked(this: TrackingCore, provider: "apple" | "google" | "email", audience: string) {
    this.track("quiz_stepper_auth_clicked", { provider, audience });
  },

  quizStepperAuthSucceeded(this: TrackingCore, provider: "apple" | "google" | "email", audience: string) {
    this.track("quiz_stepper_auth_succeeded", { provider, audience });
  },

  quizStepperAuthFailed(this: TrackingCore, provider: "apple" | "google" | "email", audience: string) {
    this.track("quiz_stepper_auth_failed", { provider, audience });
  },

  // ─── First-dinner welcome card on /dashboard ──────────────────────────────

  firstDinnerWelcomeViewed(this: TrackingCore) {
    this.track("first_dinner_welcome_viewed");
  },

  firstDinnerWelcomeCtaClicked(this: TrackingCore) {
    this.track("first_dinner_welcome_cta_clicked");
  },

  firstDinnerWelcomeDetailOpened(this: TrackingCore) {
    this.track("first_dinner_welcome_detail_opened");
  },

  // ─── Account migration (Clerk → Firebase / Apple) ─────────────────────────

  migrationPageViewed(this: TrackingCore, hasPassword: boolean, isSignedIn: boolean) {
    this.track("migration_page_viewed", {
      has_password: hasPassword,
      is_signed_in: isSignedIn,
    });
  },

  migrationAppleSignInClicked(this: TrackingCore) {
    this.track("migration_apple_sign_in_clicked");
  },

  migrationStarted(this: TrackingCore) {
    this.track("migration_started");
  },

  migrationSuccess(this: TrackingCore) {
    this.track("migration_success");
  },

  migrationError(this: TrackingCore, error: string) {
    this.track("migration_error", {
      error_message: error,
    });
  },

  migrationGoToDashboardClicked(this: TrackingCore) {
    this.track("migration_go_to_dashboard_clicked");
  },

  emailVerificationResent(this: TrackingCore) {
    this.track("email_verification_resent");
  },
};
